import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowUpRight,
  Building2,
  ChevronDown,
  Clock,
  Home as HomeIcon,
  Mail,
  MapPin,
  Menu,
  MessageCircle,
  Phone,
  Siren,
  X,
} from "lucide-react";
import { BRAND, SERVICE_INDEX } from "@/lib/site";
import { Stadtmusikanten } from "@/components/graphics/Marks";

const MAIN_NAV = [
  { label: "Startseite", to: "/", lgHide: true },
  { label: "Winterdienst", to: "/winterdienst-bremen", mega: true },
  { label: "Einsatzgebiet", to: "/einsatzgebiet" },
  { label: "Über uns", to: "/ueber-uns" },
  { label: "FAQ", to: "/faq" },
  { label: "Kontakt", to: "/kontakt" },
];

const MOBILE_NAV = [
  { label: "Startseite", to: "/" },
  { label: "Winterdienst Bremen", to: "/winterdienst-bremen" },
  { label: "Privat", to: "/winterdienst-bremen-privat" },
  { label: "Gewerbe & Hausverwaltung", to: "/winterdienst-bremen-gewerbe-hausverwaltung" },
  { label: "Leistungen", to: "/leistungen" },
  { label: "Einsatzgebiet", to: "/einsatzgebiet" },
  { label: "Über uns", to: "/ueber-uns" },
  { label: "FAQ", to: "/faq" },
  { label: "Kontakt", to: "/kontakt" },
];

const EASE = [0.22, 1, 0.36, 1];

const MegaMenu = ({ onClose }) => (
  <motion.div
    data-testid="mega-menu"
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    transition={{ duration: 0.3, ease: EASE }}
    className="absolute inset-x-0 top-full border-t border-[#DCE5EF] bg-white shadow-[0_40px_80px_-40px_rgba(6,43,82,0.4)]"
  >
    <div className="shell grid gap-10 py-10 lg:grid-cols-[0.9fr_1.4fr_0.8fr] lg:gap-14">
      <div>
        <p className="font-mono-label text-[#66788A]">/ Kundengruppe</p>
        <div className="mt-5 grid gap-3">
          <Link
            to="/winterdienst-bremen-privat"
            onClick={onClose}
            data-testid="mega-privat"
            className="group flex items-center gap-4 rounded-2xl border border-[#DCE5EF] bg-[#F5F8FC] p-4 transition-all duration-400 hover:border-[#FF6B00] hover:bg-white"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white text-[#0757B8] shadow-sm transition-colors group-hover:bg-[#FF6B00] group-hover:text-white">
              <HomeIcon className="h-5 w-5" />
            </span>
            <span>
              <span className="block text-[15px] font-bold tracking-tight">Privatkunden</span>
              <span className="mt-0.5 block text-[12px] text-[#66788A]">
                Gehweg, Eingang, Einfahrt
              </span>
            </span>
          </Link>
          <Link
            to="/winterdienst-bremen-gewerbe-hausverwaltung"
            onClick={onClose}
            data-testid="mega-gewerbe"
            className="group flex items-center gap-4 rounded-2xl bg-[#062B52] p-4 transition-transform duration-400 hover:-translate-y-0.5"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 text-[#FF6B00] transition-colors group-hover:bg-[#FF6B00] group-hover:text-white">
              <Building2 className="h-5 w-5" />
            </span>
            <span>
              <span className="block text-[15px] font-bold tracking-tight text-white">Gewerbe &amp; Hausverwaltung</span>
              <span className="mt-0.5 block text-[12px] text-white/60">
                Parkplätze, Wohnanlagen, Objekte
              </span>
            </span>
          </Link>
        </div>
      </div>

      <div>
        <div className="flex items-baseline justify-between">
          <p className="font-mono-label text-[#66788A]">/ Leistungen</p>
          <Link
            to="/leistungen"
            onClick={onClose}
            data-testid="mega-all-services"
            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#0757B8] hover:text-[#FF6B00]"
          >
            Alle ansehen <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-x-8 gap-y-1">
          {SERVICE_INDEX.map((s) => (
            <Link
              key={s.to}
              to={s.to}
              onClick={onClose}
              data-testid={`mega-link${s.to.replace(/\//g, "-")}`}
              className="group flex items-center justify-between gap-3 border-b border-[#DCE5EF]/70 py-2.5 text-[13.5px] font-medium text-[#10253A] transition-colors hover:text-[#0757B8]"
            >
              {s.title}
              <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-[#DCE5EF] transition-all group-hover:text-[#FF6B00]" />
            </Link>
          ))}
        </div>
      </div>

      <div className="relative overflow-hidden rounded-2xl bg-[#D81E2F] p-6 text-white">
        <Stadtmusikanten className="pointer-events-none absolute -right-5 bottom-0 h-36 opacity-[0.12]" color="#FFFFFF" />
        <div className="relative">
          <span className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/70" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
            </span>
            <span className="font-mono-label text-[9.5px] text-white">Notruf 24/7</span>
          </span>
          <p className="mt-3 text-[17px] font-bold leading-snug text-white">
            Schnee oder Glätte akut?
          </p>
          <p className="mt-2 text-[12px] leading-relaxed text-white/80">
            Bereitschaft für betreute Objekte in Bremen.
          </p>
          <a
            href={BRAND.phoneHref}
            data-testid="mega-phone"
            className="mt-5 flex items-center gap-2 rounded-xl bg-white px-4 py-3 text-[13.5px] font-bold text-[#D81E2F]"
          >
            <Siren className="h-4 w-4" /> {BRAND.phone}
          </a>
        </div>
      </div>
    </div>
  </motion.div>
);

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mega, setMega] = useState(false);
  const closeTimer = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 28);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMega(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const openMega = () => {
    clearTimeout(closeTimer.current);
    setMega(true);
  };
  const closeMega = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setMega(false), 160);
  };

  return (
    <>
      <header
        data-testid="site-header"
        onMouseLeave={closeMega}
        className={`fixed inset-x-0 top-[var(--banner-h)] z-50 bg-white/95 backdrop-blur-xl transition-shadow duration-500 ${
          scrolled
            ? "border-b border-[#DCE5EF] shadow-[0_10px_34px_-24px_rgba(6,43,82,0.5)]"
            : "border-b border-[#DCE5EF]/70"
        }`}
      >
        {/* ---------- Utility row (desktop, collapses on scroll) ---------- */}
        <div
          data-testid="header-utility"
          className="hidden overflow-hidden border-b border-[#DCE5EF]/70 bg-[#F5F8FC] transition-all duration-500 lg:block"
          style={{ height: scrolled ? 0 : 38, opacity: scrolled ? 0 : 1 }}
        >
          <div className="shell flex h-[38px] items-center justify-between">
            <div className="flex items-center gap-6 text-[12px] text-[#66788A]">
              <span className="flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-[#0757B8]" />
                {BRAND.street}, {BRAND.zip} {BRAND.city}
              </span>
              <span className="hidden items-center gap-1.5 xl:flex">
                <Clock className="h-3.5 w-3.5 text-[#0757B8]" />
                Winterdienst für Privat &amp; Gewerbe in Bremen
              </span>
            </div>
            <div className="flex items-center gap-6 text-[12px]">
              <a
                href={BRAND.emailHref}
                data-testid="header-utility-email"
                className="hidden items-center gap-1.5 text-[#66788A] transition-colors hover:text-[#0757B8] xl:flex"
              >
                <Mail className="h-3.5 w-3.5 text-[#0757B8]" />
                {BRAND.email}
              </a>
              <a
                href={BRAND.phoneHref}
                data-testid="header-utility-phone"
                className="flex items-center gap-1.5 font-semibold text-[#10253A] transition-colors hover:text-[#0757B8]"
              >
                <Phone className="h-3.5 w-3.5 text-[#FF6B00]" />
                {BRAND.phone}
              </a>
            </div>
          </div>
        </div>

        {/* ---------- Main row ---------- */}
        <div
          className="shell flex items-center justify-between gap-8 transition-all duration-500"
          style={{ height: scrolled ? 74 : 96 }}
        >
          <Link to="/" data-testid="header-logo" className="flex shrink-0 items-center">
            <img
              src={BRAND.logo}
              alt="Bremer Schneemann – Winterdienst Bremen Logo"
              className="w-auto transition-all duration-500"
              decoding="async"
              fetchPriority="high"
              style={{ height: scrolled ? 50 : 66, aspectRatio: "3 / 2" }}
            />
          </Link>

          <nav className="hidden items-center lg:flex lg:gap-1 xl:gap-2" data-testid="desktop-nav">
            {MAIN_NAV.map((item) =>
              item.mega ? (
                <div
                  key={item.to}
                  onMouseEnter={openMega}
                  className="relative"
                >
                  <button
                    type="button"
                    onClick={openMega}
                    data-testid="nav-winterdienst-bremen"
                    aria-expanded={mega}
                    className={`flex items-center gap-1.5 rounded-full px-3.5 py-2.5 text-[13.5px] font-medium transition-colors xl:px-4 ${
                      mega ? "bg-[#EEF6FF] text-[#0757B8]" : "text-[#10253A] hover:bg-[#F5F8FC] hover:text-[#0757B8]"
                    }`}
                  >
                    {item.label}
                    <ChevronDown
                      className={`h-3.5 w-3.5 transition-transform duration-300 ${mega ? "rotate-180" : ""}`}
                    />
                  </button>
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onMouseEnter={closeMega}
                  data-testid={`nav-${item.to === "/" ? "home" : item.to.replace(/\//g, "")}`}
                  className={({ isActive }) =>
                    `whitespace-nowrap rounded-full px-3.5 py-2.5 text-[13.5px] font-medium transition-colors xl:px-4 ${
                      item.lgHide ? "hidden xl:block" : ""
                    } ${isActive ? "bg-[#EEF6FF] text-[#0757B8]" : "text-[#10253A] hover:bg-[#F5F8FC] hover:text-[#0757B8]"}`
                  }
                >
                  {item.label}
                </NavLink>
              ),
            )}
          </nav>

          <div className="hidden shrink-0 items-center gap-2.5 lg:flex">
            <a
              href={BRAND.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-whatsapp-btn"
              aria-label="Per WhatsApp kontaktieren"
              className="btn btn-wa h-11 w-11"
            >
              <MessageCircle className="h-5 w-5" />
            </a>
            <a
              href={BRAND.phoneHref}
              data-testid="header-phone-btn"
              aria-label="Jetzt anrufen: +49 421 16152637"
              className="btn btn-ghost h-11 w-11 xl:hidden"
            >
              <Phone className="h-[18px] w-[18px]" />
            </a>
            <Link
              to="/kontakt"
              data-testid="header-cta-btn"
              className="btn btn-orange h-11 px-5 text-[13px] xl:px-6"
            >
              <span className="hidden xl:inline">Winterdienst anfragen</span>
              <span className="xl:hidden">Anfragen</span>
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="flex items-center gap-2 lg:hidden">
            <a
              href={BRAND.phoneHref}
              data-testid="mobile-header-phone"
              aria-label="Anrufen"
              className="btn btn-dark h-11 w-11"
            >
              <Phone className="h-[18px] w-[18px]" />
            </a>
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-open"
              aria-label="Menü öffnen"
              className="btn btn-ghost h-11 w-11"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>

        <AnimatePresence>{mega && <MegaMenu onClose={() => setMega(false)} />}</AnimatePresence>
      </header>

      {/* ---------- Mobile fullscreen nav ---------- */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-nav"
            className="fixed inset-0 z-[60] overflow-y-auto bg-[#062B52] text-white"
            initial={{ opacity: 0, y: -14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -14 }}
            transition={{ duration: 0.35, ease: EASE }}
          >
            <Stadtmusikanten className="pointer-events-none absolute -right-6 bottom-20 h-64 opacity-[0.07]" color="#FFFFFF" />
            <div className="shell flex h-[86px] items-center justify-between">
              <span className="font-mono-label text-[#FF6B00]">Menü</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close"
                aria-label="Menü schließen"
                className="btn h-11 w-11 border border-white/20 text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="shell pb-10">
              <nav className="flex flex-col divide-y divide-white/10 border-y border-white/10">
                {MOBILE_NAV.map((item, i) => (
                  <motion.div
                    key={item.to}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.06 + i * 0.04, duration: 0.4 }}
                  >
                    <Link
                      to={item.to}
                      data-testid={`mobile-nav-${item.to === "/" ? "home" : item.to.replace(/\//g, "")}`}
                      className="flex items-center justify-between py-4 font-semibold tracking-tight"
                      style={{ fontFamily: "Archivo, sans-serif", fontSize: 21 }}
                    >
                      {item.label}
                      <ArrowUpRight className="h-5 w-5 text-[#FF6B00]" />
                    </Link>
                  </motion.div>
                ))}
              </nav>
              <div className="mt-8 grid gap-3">
                <a href={BRAND.phoneHref} data-testid="mobile-nav-emergency" className="btn h-14 bg-[#D81E2F] text-[15px] text-white">
                  <Siren className="h-5 w-5" /> Notruf 24/7 · {BRAND.phone}
                </a>
                <Link to="/kontakt" data-testid="mobile-nav-cta" className="btn btn-orange h-14 text-[15px]">
                  Winterdienst anfragen
                </Link>
                <a
                  href={BRAND.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-nav-whatsapp"
                  className="btn btn-wa h-14 text-[15px]"
                >
                  <MessageCircle className="h-5 w-5" /> Per WhatsApp kontaktieren
                </a>
              </div>
              <p className="mt-8 text-sm text-white/55">
                {BRAND.street}
                <br />
                {BRAND.zip} {BRAND.city}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
